import { lazy } from 'react';
import {
	agencyPagesAppointment,
	agencyTemplateSms,
	dashboardPagesMenu,
	homePage,
	loginPagesMenu,
	messageUnreadPages,
	progressAgency,
	progressOperator,
	requestLeadPage,
	stripePages,
	unloggedPages,
} from '../menu';
import Login from '../pages/presentation/auth/Login';
import { RouteDefinition } from '../types/route-definition-type';

const Components = {
	Public: {
		HOMEPAGE: lazy(() => import('../layout/Page/Homepage')),
		HOME: lazy(() => import('../pages/presentation/agency/agency-home/AgencyHomePage')),
		GENERIC_LANDING: lazy(
			() => import('../pages/presentation/admin/admin-template/TemplateLeadPage'),
		),
		TEMP_PAGE: lazy(() => import('../pages/presentation/admin/admin-template/TemplateTempPage')),
	},

	// Componenti di autenticazione
	Auth: {
		LOGIN: Login,
		PAGE_404: lazy(() => import('../pages/presentation/auth/Page404')),
		PAYMENT_SUCCESS_PAGE: lazy(() => import('../pages/presentation/auth/PaymentSuccess')),
	},

	// Componenti Admin
	Admin: {
		DASHBOARD: lazy(() => import('../pages/presentation/admin/admin-lead/LeadPage')),
		SEARCH_LEAD: lazy(() => import('../pages/presentation/admin/admin-lead/LeadTable')),
		CREATE_TEMPLATE: lazy(
			() => import('../pages/presentation/admin/admin-template/TemplateCreatePage'),
		),
		UPDATE_TEMPLATE: lazy(
			() => import('../pages/presentation/admin/admin-template/TemplateUpdatePage'),
		),
		AGENCY_PAGE: lazy(
			() => import('../pages/presentation/admin/admin-agency-create-view/AgencySignupPage'),
		),
		MESSAGE_UNREAD_PAGE: lazy(
			() => import('../pages/presentation/admin/admin-message-unread/MessagePage'),
		),
		OPERATORS_PAGE: lazy(
			() => import('../pages/presentation/admin/admin-operator/OperatoreSignupPage'),
		),
		GROUPS_PAGE: lazy(() => import('../pages/presentation/admin/admin-groups/GroupsPage')),
		REPORT_OPERATOR_PAGE: lazy(
			() => import('../pages/presentation/admin/admin-report/OperatorReportPage'),
		),
		PROGRESS_OPERATOR_PAGE: lazy(
			() => import('../pages/presentation/admin/operator-progress/OperatorProgressPage'),
		),
		ADMIN_STATS: lazy(() => import('../pages/presentation/admin/admin-stats/AdminStatsPage')),
		ADMIN_SUPPORT_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-assistenza/AgencySupportAdminPage'),
		),
		CHANNEL_MANAGEMENT: lazy(
			() => import('../pages/presentation/admin/admin-channels/ChannelManagementPage'),
		),
		EMAIL_CONFIG_PAGE: lazy(() => import('../pages/presentation/admin/admin-email/EmailConfigPage')),
		EMAIL_TEMPLATE_PAGE: lazy(() => import('../pages/presentation/admin/admin-email/EmailTemplatePage')),
		EMAIL_SEND_PAGE: lazy(() => import('../pages/presentation/admin/admin-email/EmailSendPage')),
		EMAIL_HISTORY_PAGE: lazy(() => import('../pages/presentation/admin/admin-email-history/EmailHistoryPage')),
	},

	// Componenti Agenzia
	Agency: {
		AGENCY_LEAD_PAGE: lazy(
			() => import('../pages/presentation/customer/customer-home/CustomerLeadPage'),
		),
		AGENCY_REPORT_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-report/AgencyReportPage'),
		),
		AGENCY_TEMPLATE_SMS: lazy(
			() => import('../pages/presentation/agency/agency-template-sms/AgencyTemplateSmsPage'),
		),
		AGENCY_APPOINTMENTS: lazy(
			() => import('../pages/presentation/agency/agency-appuntamenti/AgencyAppointmentPage'),
		),
		AGENCY_ESITO: lazy(
			() => import('../pages/presentation/customer/customer-esiti/CustomerEsitiPage'),
		),
		AGENCY_PRATICHE_LEAD: lazy(
			() => import('../pages/presentation/agency/agency-pratiche/AgencyPratichePage'),
		),
		PRATICHE_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-pratiche/AgencyPraticheTablePage'),
		),
		MASSIVE_SMS_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-template-sms/AgencySendMassiveSmsPage'),
		),
		PAYMENT_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-payment/PaymentChargePage'),
		),
		UPLOAD_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-upload-leads/AgencyUploadLeadPage'),
		),
		UPLOADED_FILE_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-upload-leads/AgencyFileUploadedPage'),
		),
		AGENCY_SUPPORT_PAGE: lazy(
			() => import('../pages/presentation/agency/agency-assistenza/AgencySupportPage'),
		),
		COLLABORATOR_SIGNUP_PAGE: lazy(
			() => import('../pages/presentation/agency/collaborators/CollaboratorSignupPage'),
		),
		COLLABORATOR_STATS: lazy(
			() => import('../pages/presentation/agency/collaborators/stats/CollaboratorStatsPage'),
		),
		LISTINI_PAGE: lazy(() => import('../pages/presentation/listino/ListiniPage')),
		AGENCY_SETTINGS: lazy(
			() => import('../pages/presentation/agency/agency-settings/rules/RuleConditionPage'),
		),
	},

	// Componenti Cliente
	Customer: {
		CUSTOMER_REQUEST_LEAD_PAGE: lazy(
			() => import('../pages/presentation/customer/customer-request-lead/CustomerRequestLeadPage'),
		),
	},

	// Componenti generali
	General: {
		SETTINGS: lazy(() => import('../pages/presentation/settings/SettingsPage')),
		SUBSCRIPTIONS: lazy(() => import('../pages/presentation/subscriptions/SubscriptionPage')),
	},

	// Componenti statistiche
	Stats: {
		GENERAL_STATS_KENA: lazy(
			() => import('../pages/presentation/agency/agency-pratiche/kenan/stats/StatsSelector'),
		),
	},
};

const routes: RouteDefinition[] = [
	{
		id: 'homepage',
		path: homePage.login.path,
		element: <Components.Public.HOMEPAGE />,
	},
	{
		id: 'home',
		path: '/home',
		element: <Components.Public.HOME />,
	},
	{
		id: 'generic-landing',
		path: 'public/landing',
		element: <Components.Public.GENERIC_LANDING />,
	},
	{
		id: 'temp-page',
		path: 'public/landing-temp/:key',
		element: <Components.Public.TEMP_PAGE />,
	},

	//  di autenticazione
	{
		id: 'login',
		path: loginPagesMenu.login.path,
		element: <Components.Auth.LOGIN />,
	},
	{
		id: '404',
		path: unloggedPages.page404.path,
		element: <Components.Auth.PAGE_404 />,
	},
	{
		id: 'payment-success',
		path: stripePages.paymentSuccess.path,
		element: <Components.Auth.PAYMENT_SUCCESS_PAGE />,
	},

	//  Admin
	{
		id: 'dashboard',
		path: dashboardPagesMenu.dashboard.path,
		element: <Components.Admin.DASHBOARD />,
	},
	{
		id: 'leads-table',
		path: 'leads-table',
		element: <Components.Admin.SEARCH_LEAD />,
	},
	{
		id: 'groups-page',
		path: dashboardPagesMenu.groups.path,
		element: <Components.Admin.GROUPS_PAGE />,
	},
	{
		id: 'create-template',
		path: dashboardPagesMenu.template.path,
		element: <Components.Admin.CREATE_TEMPLATE />,
	},
	{
		id: 'update-template',
		path: dashboardPagesMenu.updateTemplate.path,
		element: <Components.Admin.UPDATE_TEMPLATE />,
	},
	{
		id: 'agency-page',
		path: dashboardPagesMenu.agency.path,
		element: <Components.Admin.AGENCY_PAGE />,
	},
	{
		id: 'message-unread',
		path: messageUnreadPages.message.path,
		element: <Components.Admin.MESSAGE_UNREAD_PAGE />,
	},
	{
		id: 'operators',
		path: dashboardPagesMenu.operators.path,
		element: <Components.Admin.OPERATORS_PAGE />,
	},
	{
		id: progressOperator.progress.id,
		path: progressOperator.progress.path,
		element: <Components.Admin.PROGRESS_OPERATOR_PAGE />,
	},
	{
		id: 'stats',
		path: 'stats',
		element: <Components.Admin.ADMIN_STATS />,
	},
	{
		id: 'admin-support',
		path: 'admin-support',
		element: <Components.Admin.ADMIN_SUPPORT_PAGE />,
	},
	{
		id: 'channel-management',
		path: 'channel-management',
		element: <Components.Admin.CHANNEL_MANAGEMENT />,
	},
	{
		id: 'email-config',
		path: dashboardPagesMenu.emailConfig.path,
		element: <Components.Admin.EMAIL_CONFIG_PAGE />,
	},
	{
		id: 'template-email',
		path: dashboardPagesMenu.templateEmail.path,
		element: <Components.Admin.EMAIL_TEMPLATE_PAGE />,
	},
	{
		id: 'massive-email',
		path: dashboardPagesMenu.sendMassiveEmail.path,
		element: <Components.Admin.EMAIL_SEND_PAGE />,
	},
	{
		id: 'email-history',
		path: dashboardPagesMenu.emailHistory.path,
		element: <Components.Admin.EMAIL_HISTORY_PAGE />,
	},

	//  Agency
	{
		id: 'agency-lead',
		path: 'agency-lead',
		element: <Components.Agency.AGENCY_LEAD_PAGE />,
	},
	{
		id: progressAgency.progress.id,
		path: progressAgency.progress.path,
		element: <Components.Agency.AGENCY_REPORT_PAGE />,
	},
	{
		id: agencyTemplateSms.templateSms.id,
		path: agencyTemplateSms.templateSms.path,
		element: <Components.Agency.AGENCY_TEMPLATE_SMS />,
	},
	{
		id: agencyPagesAppointment.appointment.id,
		path: agencyPagesAppointment.appointment.path,
		element: <Components.Agency.AGENCY_APPOINTMENTS />,
	},
	{
		id: 'agencyEsiti',
		path: 'esiti',
		element: <Components.Agency.AGENCY_ESITO />,
	},
	{
		id: 'praticheLead',
		path: 'pratiche/lead/:uuid',
		element: <Components.Agency.AGENCY_PRATICHE_LEAD />,
	},
	{
		id: 'pratiche',
		path: 'pratiche',
		element: <Components.Agency.PRATICHE_PAGE />,
	},
	{
		id: 'massive-sms',
		path: 'massive-sms',
		element: <Components.Agency.MASSIVE_SMS_PAGE />,
	},
	{
		id: stripePages.paymentCharge.id,
		path: stripePages.paymentCharge.path,
		element: <Components.Agency.PAYMENT_PAGE />,
	},
	{
		id: 'upload',
		path: 'upload-file',
		element: <Components.Agency.UPLOAD_PAGE />,
	},
	{
		id: 'file-uploaded',
		path: 'file-uploaded',
		element: <Components.Agency.UPLOADED_FILE_PAGE />,
	},
	{
		id: 'support',
		path: 'support',
		element: <Components.Agency.AGENCY_SUPPORT_PAGE />,
	},
	{
		id: 'collaborator',
		path: 'collaborator',
		element: <Components.Agency.COLLABORATOR_SIGNUP_PAGE />,
	},
	{
		id: 'collaborator/stats',
		path: 'collaborator/stats',
		element: <Components.Agency.COLLABORATOR_STATS />,
	},
	{
		id: 'listini',
		path: 'listini',
		element: <Components.Agency.LISTINI_PAGE />,
	},
	{
		id: 'settings-agency',
		path: 'settings-agency',
		element: <Components.Agency.AGENCY_SETTINGS />,
	},

	//  Customer
	{
		id: 'customer-request-lead',
		path: requestLeadPage.requestLead.path,
		element: <Components.Customer.CUSTOMER_REQUEST_LEAD_PAGE />,
	},

	// generali
	{
		id: 'settings',
		path: 'my-settings',
		element: <Components.General.SETTINGS />,
	},
	{
		id: 'subscriptions',
		path: 'subscriptions',
		element: <Components.General.SUBSCRIPTIONS />,
	},

	//  statistiche
	{
		id: 'advanced-kena-stats',
		path: 'advanced-kena-stats',
		element: <Components.Stats.GENERAL_STATS_KENA />,
	},

	// {
	//   id: 'reports',
	//   path: dashboardPagesMenu.report.path,
	//   element: <Components.Admin.REPORT_OPERATOR_PAGE />,
	// },
];

export default routes;
